import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { API } from '@/App';
import { 
  Settings, 
  FileText, 
  Save, 
  RefreshCw,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Scale,
  Brain,
  CheckCircle,
  AlertTriangle,
  PieChart,
  Target,
  BarChart3,
  Layers
} from 'lucide-react';

const ExamConfigManager = ({ getAuthHeaders }) => {
  const [configs, setConfigs] = useState([]);
  const [selectedExam, setSelectedExam] = useState('');
  const [examDetail, setExamDetail] = useState(null);
  const [pipelineConfig, setPipelineConfig] = useState(null);
  const [coverageStats, setCoverageStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedPrompt, setExpandedPrompt] = useState(null);
  
  // Form states
  const [editedPrompts, setEditedPrompts] = useState({});
  const [editedDistributions, setEditedDistributions] = useState({});
  const [editedRules, setEditedRules] = useState({});

  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    const headers = getAuthHeaders();
    const response = await fetch(`${API}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
        ...options.headers,
      },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }
    return response.json();
  }, [getAuthHeaders]);

  const loadConfigs = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/exam-configs');
      setConfigs(data);
      if (data.length > 0 && !selectedExam) {
        setSelectedExam(data[0].exam_id);
      }
    } catch (error) {
      console.error('Failed to load configs:', error);
      toast.error('Fehler beim Laden der Konfigurationen');
    }
  }, [fetchWithAuth, selectedExam]);

  const loadExamDetail = useCallback(async () => {
    if (!selectedExam) return;
    setLoading(true);
    try {
      const data = await fetchWithAuth(`/exam-configs/${selectedExam}`);
      setExamDetail(data);
      
      // Initialize edit states
      setEditedPrompts(data.prompts || {});
      setEditedDistributions({
        difficulty: data.difficulty_distribution || {},
        cognitive: data.cognitive_distribution || {}
      });
      setEditedRules(data.validation_rules || {});
      
      // Load pipeline config for domain distributions
      try {
        const pipelineData = await fetchWithAuth(`/question-engine/pipelines/${selectedExam}/config`);
        setPipelineConfig(pipelineData);
      } catch (e) {
        setPipelineConfig(null);
      }
      
      // Load coverage statistics
      try {
        const coverageData = await fetchWithAuth(`/question-engine/coverage/${selectedExam}`);
        setCoverageStats(coverageData);
      } catch (e) {
        setCoverageStats(null);
      }
    } catch (error) {
      console.error('Failed to load exam detail:', error);
      toast.error('Fehler beim Laden der Prüfungsdetails');
    } finally {
      setLoading(false);
    }
  }, [selectedExam, fetchWithAuth]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  useEffect(() => {
    if (selectedExam) {
      loadExamDetail();
    }
  }, [selectedExam, loadExamDetail]);

  const savePrompt = async (phase) => {
    if (!editedPrompts[phase]?.content) {
      toast.error('Prompt-Inhalt darf nicht leer sein');
      return;
    }
    
    setSaving(true);
    try {
      await fetchWithAuth(`/exam-configs/${selectedExam}/prompts`, {
        method: 'PUT',
        body: JSON.stringify({
          phase,
          content: editedPrompts[phase].content,
          version: editedPrompts[phase].version || '1.0'
        }),
      });
      toast.success(`${phase} Prompt gespeichert`);
      loadExamDetail();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const saveValidationRules = async () => {
    setSaving(true);
    try {
      await fetchWithAuth(`/exam-configs/${selectedExam}/validation-rules`, {
        method: 'PUT',
        body: JSON.stringify(editedRules),
      });
      toast.success('Validierungsregeln gespeichert');
      loadExamDetail();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const getPromptStatusBadge = (hasPrompt) => {
    return hasPrompt ? (
      <Badge className="bg-green-500 text-white"><CheckCircle className="w-3 h-3 mr-1" />Konfiguriert</Badge>
    ) : (
      <Badge className="bg-yellow-500 text-white"><AlertTriangle className="w-3 h-3 mr-1" />Standard</Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="exam-config-manager">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Exam Configuration Manager
          </h2>
          <p className="text-sm text-gray-500">Multi-Exam AI Engine Konfiguration</p>
        </div>
        <div className="flex gap-2">
          <select 
            value={selectedExam} 
            onChange={(e) => setSelectedExam(e.target.value)}
            className="p-2 border rounded text-sm"
            data-testid="exam-config-selector"
          >
            <option value="">-- Prüfung wählen --</option>
            {configs.map(config => (
              <option key={config.exam_id} value={config.exam_id}>
                {config.exam_id} - {config.name}
              </option>
            ))}
          </select>
          <Button onClick={loadExamDetail} variant="outline" size="sm" disabled={!selectedExam || loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {examDetail && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-6 w-full max-w-2xl">
            <TabsTrigger value="overview">Übersicht</TabsTrigger>
            <TabsTrigger value="domains">Domains</TabsTrigger>
            <TabsTrigger value="coverage">Coverage</TabsTrigger>
            <TabsTrigger value="prompts">Prompts</TabsTrigger>
            <TabsTrigger value="rules">Regeln</TabsTrigger>
            <TabsTrigger value="sections">Sektionen</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Normative Sources Card - Extended */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Normative Quellen
                  </CardTitle>
                  <CardDescription>
                    Alle verknüpften Dokumente für diese Prüfung
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Primary Source */}
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-blue-500 text-white">Primär</Badge>
                        <span className="font-medium">{examDetail.normative_source}</span>
                      </div>
                      {examDetail.normative_source_version && (
                        <p className="text-sm text-gray-600">Version: {examDetail.normative_source_version}</p>
                      )}
                    </div>
                    
                    {/* KB Documents / Extended Normative Sources */}
                    {examDetail.normative_sources?.length > 0 ? (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">Verknüpfte KB-Dokumente ({examDetail.linked_kb_documents || examDetail.normative_sources.length})</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {examDetail.normative_sources.map((source, idx) => (
                            <div key={source.source_id || source.name || `source-${idx}`} className="p-2 border rounded-lg hover:bg-gray-50 transition-colors">
                              <div className="flex items-center gap-2">
                                <Badge 
                                  variant="outline" 
                                  className={
                                    source.type === 'primary' ? 'border-blue-400 text-blue-700' :
                                    source.type === 'glossary' ? 'border-purple-400 text-purple-700' :
                                    source.type === 'practice_guide' ? 'border-green-400 text-green-700' :
                                    source.type === 'supplementary' ? 'border-orange-400 text-orange-700' :
                                    'border-gray-400 text-gray-700'
                                  }
                                >
                                  {source.type === 'primary' ? 'Primär' :
                                   source.type === 'glossary' ? 'Glossar' :
                                   source.type === 'practice_guide' ? 'Practice Guide' :
                                   source.type === 'supplementary' ? 'Ergänzend' :
                                   source.type === 'standard' ? 'Standard' :
                                   source.type === 'framework' ? 'Framework' : source.type}
                                </Badge>
                                <span className="text-sm font-medium truncate">{source.name}</span>
                              </div>
                              {source.version && (
                                <p className="text-xs text-gray-500 mt-1 ml-1">Version: {source.version}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : examDetail.secondary_sources?.length > 0 ? (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">Sekundäre Quellen</h4>
                        <div className="flex flex-wrap gap-2">
                          {examDetail.secondary_sources.map((source, idx) => (
                            <Badge key={`secondary-${source}-${idx}`} variant="outline">{source}</Badge>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                        <p>Keine zusätzlichen Dokumente verknüpft.</p>
                        <p className="text-xs mt-1">Laden Sie Dokumente im Evidence-Tab hoch, um sie mit dieser Prüfung zu verknüpfen.</p>
                      </div>
                    )}
                    
                    <div className="text-sm text-gray-500 pt-2 border-t">
                      Sprache: {examDetail.language === 'en' ? 'Englisch' : examDetail.language === 'de' ? 'Deutsch' : examDetail.language}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Prompt Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span>Question Factory</span>
                      {getPromptStatusBadge(examDetail.has_question_factory_prompt)}
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Academic Validator</span>
                      {getPromptStatusBadge(examDetail.has_academic_validator_prompt)}
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Didactic Evaluator</span>
                      {getPromptStatusBadge(examDetail.has_didactic_evaluator_prompt)}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Scale className="w-5 h-5" />
                    Schwierigkeitsverteilung
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-green-600">Niedrig</span>
                      <span>{examDetail.difficulty_distribution?.low || 0}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-yellow-600">Mittel</span>
                      <span>{examDetail.difficulty_distribution?.medium || 0}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-600">Hoch</span>
                      <span>{examDetail.difficulty_distribution?.high || 0}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Brain className="w-5 h-5" />
                    Kognitive Verteilung
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Recall</span>
                      <span>{examDetail.cognitive_distribution?.recall || 0}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Understand</span>
                      <span>{examDetail.cognitive_distribution?.understand || 0}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Apply</span>
                      <span>{examDetail.cognitive_distribution?.apply || 0}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Analyze</span>
                      <span>{examDetail.cognitive_distribution?.analyze || 0}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Unterstützte Fragetypen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {examDetail.supported_question_types?.map(type => (
                    <Badge key={type} variant="secondary">{type}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Domains Tab - ECO and Performance Domain Distribution */}
          <TabsContent value="domains" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* ECO Domains */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-500" />
                    ECO Domains (Prüfungsbereiche)
                  </CardTitle>
                  <CardDescription>
                    Gewichtete Verteilung der Prüfungsinhalte
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pipelineConfig?.eco_domains?.length > 0 ? (
                    <div className="space-y-3">
                      {pipelineConfig.eco_domains.map((domain, idx) => (
                        <div key={`eco-${domain.name || idx}`} className="space-y-1">
                          <div className="flex justify-between items-center text-sm">
                            <span className="font-medium">{domain.name}</span>
                            <Badge variant="secondary">{domain.weight_percentage}%</Badge>
                          </div>
                          <Progress value={domain.weight_percentage} className="h-2" />
                          {domain.tasks?.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              {domain.tasks.length} Aufgaben definiert
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-4">
                      <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Keine ECO-Domains konfiguriert</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Performance Domains */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-green-500" />
                    Performance Domains
                  </CardTitle>
                  <CardDescription>
                    PMBOK 7 / Zertifizierungsspezifische Bereiche
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pipelineConfig?.performance_domains?.length > 0 ? (
                    <div className="space-y-3">
                      {pipelineConfig.performance_domains.map((domain, idx) => (
                        <div key={`perf-${domain.name || idx}`} className="space-y-1">
                          <div className="flex justify-between items-center text-sm">
                            <span className="font-medium">{domain.name}</span>
                            <Badge variant="secondary">{domain.weight_percentage}%</Badge>
                          </div>
                          <Progress value={domain.weight_percentage * 2} className="h-2" />
                          {domain.principles?.length > 0 && (
                            <div className="text-xs text-gray-500">
                              Prinzipien: {domain.principles.join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-4">
                      <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Keine Performance Domains (nicht für alle Zertifizierungen)</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Question Type Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-purple-500" />
                  Fragetyp-Verteilung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { key: 'single', label: 'Single Choice', color: 'bg-blue-500' },
                    { key: 'multiple', label: 'Multiple Choice', color: 'bg-green-500' },
                    { key: 'true_false', label: 'True/False', color: 'bg-yellow-500' },
                    { key: 'matching', label: 'Matching', color: 'bg-purple-500' }
                  ].map(type => {
                    const value = examDetail.question_type_distribution?.[type.key] || 0;
                    return (
                      <div key={type.key} className="text-center p-3 border rounded-lg">
                        <div className={`w-12 h-12 mx-auto rounded-full ${type.color} flex items-center justify-center text-white font-bold mb-2`}>
                          {Math.round(value * 100)}%
                        </div>
                        <div className="text-sm font-medium">{type.label}</div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Coverage Tab - Question Distribution Analysis */}
          <TabsContent value="coverage" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-indigo-500" />
                  Coverage-Analyse
                </CardTitle>
                <CardDescription>
                  Wie gut decken die generierten Fragen die Prüfungsbereiche ab?
                </CardDescription>
              </CardHeader>
              <CardContent>
                {coverageStats ? (
                  <div className="space-y-4">
                    {/* Overall Coverage */}
                    <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">Gesamtabdeckung</span>
                        <span className="text-2xl font-bold text-indigo-600">
                          {coverageStats.overall_coverage || 0}%
                        </span>
                      </div>
                      <Progress value={coverageStats.overall_coverage || 0} className="h-3" />
                    </div>

                    {/* Domain Coverage */}
                    {coverageStats.domain_coverage?.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-medium">Domain-Abdeckung</h4>
                        {coverageStats.domain_coverage.map((domain, idx) => (
                          <div key={`cov-${domain.name || idx}`} className="flex items-center gap-2">
                            <span className="w-32 text-sm truncate">{domain.name}</span>
                            <Progress value={domain.coverage} className="flex-1 h-2" />
                            <span className="text-sm w-12 text-right">{domain.coverage}%</span>
                            <span className="text-xs text-gray-500 w-16">
                              ({domain.questions}/{domain.target})
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Stats Grid */}
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                          {coverageStats.total_questions || 0}
                        </div>
                        <div className="text-sm text-gray-600">Fragen gesamt</div>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                          {coverageStats.akus_covered || 0}
                        </div>
                        <div className="text-sm text-gray-600">AKUs abgedeckt</div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">
                          {coverageStats.gaps_identified || 0}
                        </div>
                        <div className="text-sm text-gray-600">Lücken identifiziert</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <PieChart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">Keine Coverage-Daten verfügbar</p>
                    <p className="text-sm mt-1">
                      Generieren Sie zuerst Fragen, um die Abdeckung zu analysieren
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recommendations */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Empfehlungen</CardTitle>
              </CardHeader>
              <CardContent>
                {coverageStats?.recommendations?.length > 0 ? (
                  <ul className="space-y-2">
                    {coverageStats.recommendations.map((rec, idx) => (
                      <li key={`rec-${idx}-${(rec || '').slice(0, 20)}`} className="flex items-start gap-2 text-sm">
                        <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">
                    {coverageStats ? 
                      'Keine Empfehlungen - die Abdeckung ist gut!' :
                      'Erstellen Sie Fragen, um Empfehlungen zu erhalten'
                    }
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Prompts Tab */}
          <TabsContent value="prompts" className="space-y-4">
            {['question_factory', 'academic_validator', 'didactic_evaluator'].map(phase => (
              <Card key={phase}>
                <CardHeader 
                  className="cursor-pointer"
                  onClick={() => setExpandedPrompt(expandedPrompt === phase ? null : phase)}
                >
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg capitalize">
                      {phase.replace('_', ' ')}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getPromptStatusBadge(examDetail.prompts?.[phase]?.content)}
                      {expandedPrompt === phase ? <ChevronUp /> : <ChevronDown />}
                    </div>
                  </div>
                  {examDetail.prompts?.[phase]?.version && (
                    <CardDescription>Version: {examDetail.prompts[phase].version}</CardDescription>
                  )}
                </CardHeader>
                
                {expandedPrompt === phase && (
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Version</Label>
                      <Input
                        value={editedPrompts[phase]?.version || '1.0'}
                        onChange={(e) => setEditedPrompts(prev => ({
                          ...prev,
                          [phase]: { ...prev[phase], version: e.target.value }
                        }))}
                        className="mt-1 w-32"
                      />
                    </div>
                    <div>
                      <Label>Prompt Inhalt</Label>
                      <Textarea
                        value={editedPrompts[phase]?.content || ''}
                        onChange={(e) => setEditedPrompts(prev => ({
                          ...prev,
                          [phase]: { ...prev[phase], content: e.target.value }
                        }))}
                        rows={15}
                        className="mt-1 font-mono text-sm"
                        placeholder="Prompt eingeben..."
                      />
                    </div>
                    <Button 
                      onClick={() => savePrompt(phase)} 
                      disabled={saving}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Speichern...' : 'Speichern'}
                    </Button>
                  </CardContent>
                )}
              </Card>
            ))}
          </TabsContent>

          {/* Validation Rules Tab */}
          <TabsContent value="rules" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Akademische Validierung</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Max. Wiederholungen</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={editedRules.academic?.max_retries || 3}
                      onChange={(e) => setEditedRules(prev => ({
                        ...prev,
                        academic: { ...prev.academic, max_retries: parseInt(e.target.value) }
                      }))}
                      className="mt-1 w-24"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Exakte Terminologie erforderlich</Label>
                    <Switch
                      checked={editedRules.academic?.require_exact_terminology ?? true}
                      onCheckedChange={(checked) => setEditedRules(prev => ({
                        ...prev,
                        academic: { ...prev.academic, require_exact_terminology: checked }
                      }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Interpretation erlauben</Label>
                    <Switch
                      checked={editedRules.academic?.allow_interpretation ?? false}
                      onCheckedChange={(checked) => setEditedRules(prev => ({
                        ...prev,
                        academic: { ...prev.academic, allow_interpretation: checked }
                      }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Referenz erforderlich</Label>
                    <Switch
                      checked={editedRules.academic?.reference_required ?? true}
                      onCheckedChange={(checked) => setEditedRules(prev => ({
                        ...prev,
                        academic: { ...prev.academic, reference_required: checked }
                      }))}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Didaktische Validierung</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Max. Wiederholungen</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={editedRules.didactic?.max_retries || 2}
                      onChange={(e) => setEditedRules(prev => ({
                        ...prev,
                        didactic: { ...prev.didactic, max_retries: parseInt(e.target.value) }
                      }))}
                      className="mt-1 w-24"
                    />
                  </div>
                  <div>
                    <Label>Min. Qualitätsscore (1-10)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={10}
                      value={editedRules.didactic?.min_quality_score || 7}
                      onChange={(e) => setEditedRules(prev => ({
                        ...prev,
                        didactic: { ...prev.didactic, min_quality_score: parseInt(e.target.value) }
                      }))}
                      className="mt-1 w-24"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Recall-Fragen erlauben</Label>
                    <Switch
                      checked={editedRules.didactic?.allow_recall_questions ?? true}
                      onCheckedChange={(checked) => setEditedRules(prev => ({
                        ...prev,
                        didactic: { ...prev.didactic, allow_recall_questions: checked }
                      }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Semantische Redundanz prüfen</Label>
                    <Switch
                      checked={editedRules.didactic?.check_semantic_redundancy ?? true}
                      onCheckedChange={(checked) => setEditedRules(prev => ({
                        ...prev,
                        didactic: { ...prev.didactic, check_semantic_redundancy: checked }
                      }))}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
            <Button onClick={saveValidationRules} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Speichern...' : 'Regeln speichern'}
            </Button>
          </TabsContent>

          {/* Sections Tab */}
          <TabsContent value="sections" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Prüfungssektionen ({examDetail.sections?.length || 0})</CardTitle>
                <CardDescription>
                  Gewichtete Sektionen für die Fragenverteilung
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {examDetail.sections?.map(section => (
                    <div key={section.section_id} className="p-3 border rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{section.name}</span>
                        <Badge variant="secondary">{section.weight}%</Badge>
                      </div>
                      {section.topics?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {section.topics.map(topic => (
                            <Badge key={topic} variant="outline" className="text-xs">{topic}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {examDetail.terminology_constraints?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Terminologie ({examDetail.terminology_constraints.length})</CardTitle>
                  <CardDescription>
                    Erlaubte und verbotene Begriffe
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {examDetail.terminology_constraints.map((term, idx) => (
                      <div key={`term-${term.term || idx}`} className="p-2 border rounded text-sm">
                        <span className="font-medium text-green-700">{term.term}</span>
                        {term.prohibited?.length > 0 && (
                          <span className="text-red-500 ml-2">
                            ✗ {term.prohibited.join(', ')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Database Maintenance moved to System Maintenance Tab in Admin Panel */}

      {!examDetail && selectedExam && (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            {loading ? 'Lade Konfiguration...' : 'Keine Konfiguration gefunden'}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExamConfigManager;
